import { Collection } from "./collections";
import { Shopify } from "./shopify";

// Represents the price of something
export class Amount {
    amount: number;
    currencyCode: string;

    constructor(amount: number, currencyCode: string) {
        this.amount = amount;
        this.currencyCode = currencyCode;
    }
}

// Represents a shopify product variant
export class Variant {
    id: string;
    price: Amount;
    selectedOptions: Array<{name: string, value: string}> = [];

    constructor(id: string, price: Amount, selectedOptions: Array<{name: string, value: string}>) {
        this.id = id;
        this.price = price;
        this.selectedOptions = selectedOptions;
    }
}

// Represents a metafield identifier
// This is used to identify a metafield by its namespace and key
export class MetafieldIdentifier {
    namespace: string;
    key: string;
    constructor(namespace: string, key: string) {
        this.namespace = namespace;
        this.key = key;
    }

    toString() : string {
        return `${this.namespace}.${this.key}`;
    }
}

// Represents a shopify metafield (the parts we use)
// see: https://shopify.dev/docs/api/storefront/2025-07/objects/Metafield
export class Metafield {
    identifier: MetafieldIdentifier;
    referenceType: string;

    constructor(namespace: string, key: string, referenceType: string) {
        this.identifier = new MetafieldIdentifier(namespace, key);
        this.referenceType = referenceType;
    }

    toString() : string {
        return `Metafield(${this.identifier.toString()}, ${this.referenceType})`;
    }
}

// Represents a named color with a hex value
export class NamedColor {
    name: string;
    hex: string;

    constructor(name: string, hex: string) {
        this.name = name;
        this.hex = hex;
    }

    toString() : string {
        return `${this.name} (${this.hex})`;
    }
}

// Represents a set of colors for a product
// This is used for product color options, like card stock color, ink color, etc.
export class ProductColors {
    colors: Array<NamedColor> = new Array<NamedColor>();
}

// Represents a shopify product
export class Product {
    name: string;
    description: string;
    price: Amount;
    handle: string;
    featuredImageThumb: string | null = null;
    featuredImageHero: string | null = null;
    featuredVariant: Variant | null = null;
    featuredOption: Map<string, Set<string>> | null = null;
    images: Array<string> = [];
    variants: Map<string, Variant> = new Map<string, Variant>(); // Map of variant id to variant object
    options: Map<string, Set<string>> = new Map<string, Set<string>>(); // Key: option name, Value: array of option values
    metafields: Map<string, Metafield> = new Map<string, Metafield>(); // Key: metafield identifier toString(), Value: metafield object

    private _cachedProductColors: Map<string, ProductColors> | null = null; // Key: metafield identifier toString()

    constructor(name: string, description: string, price: Amount, handle: string) {
        this.name = name;
        this.description = description;
        this.price = price;
        this.handle = handle;
    }

    async getVariants() : Promise<Map<string, Variant>> {
        if(this.variants.size > 0) {
            return this.variants;
        }

        const allVariants: Map<string, Variant> = await getVariantsForProduct(this.handle);
        if(allVariants === undefined || allVariants === null) {
            throw new Error("No variants found for product: " + this.handle);
        }

        this.variants = allVariants;

        return this.variants;
    }

    async getVariantWithOptions(variantOptions: Array<{name: string, value: string}>) : Promise<Variant> {

        // Try the featured variant first
        if(this.featuredVariant !== null) {
            let match = true;
            for(let j = 0; j < this.featuredVariant.selectedOptions.length; j++) {
                const selectedOption = this.featuredVariant.selectedOptions[j];
                if(selectedOption.name !== variantOptions[j].name || selectedOption.value !== variantOptions[j].value) {
                    match = false;
                    break;
                }
            }
            if(match) {
                return this.featuredVariant;
            }
        }

        const allVariants = await this.getVariants();

        for(let variant of allVariants.values()) {
            if(variant.selectedOptions.length !== variantOptions.length) {
                continue;
            }

            let match = true;
            for(let j = 0; j < variant.selectedOptions.length; j++) {
                const selectedOption = variant.selectedOptions[j];
                if(selectedOption.name !== variantOptions[j].name || selectedOption.value !== variantOptions[j].value) {
                    match = false;
                    break;
                }
            }

            if(match) {
                return variant;
            }
        }
        throw new Error("Could not find variant with options: " + JSON.stringify(variantOptions));
    }

    async getOptions() : Promise<Map<string, Set<string>>> {
        if(this.options.size > 0) {
            return this.options;
        }

        const allVariants = await this.getVariants();

        // Populate the options map
        for(let variant of allVariants.values()) {
            for(let j = 0; j < variant.selectedOptions.length; j++) {
                const selectedOption = variant.selectedOptions[j];
                if(!this.options.has(selectedOption.name)) {
                    this.options.set(selectedOption.name, new Set<string>());
                }
                this.options.get(selectedOption.name)?.add(selectedOption.value);
            }
        }

        return this.options;
    }

    async getQuantities() : Promise<Set<string>> {
        // Get options for the product
        const options = await this.getOptions();
        // Get all the options for the key "Quantity"
        const quantityOptions = options.get("Quantity");
        if(quantityOptions === undefined || quantityOptions === null) {
            throw new Error("No quantity options found for product: " + this.handle);
        }
        return quantityOptions;
    }

    async getColors() : Promise<Map<string, ProductColors>> {
        const colors = new Map<string, ProductColors>();

        // Fetch the colors from the metafield
        // Each metafield is a reference to a metaobject of type "set_of_colors"
        // Each "set_of_colors" metaobject has a "colors" field which is a reference list of metaobjects of type "named_color"
        // Each "named_color" metaobject has a "color_name" text field and "color" color field
        const query = `
            query getMetafieldColors($handle: String!, $metafieldIdentifiers: [HasMetafieldsIdentifier!]!) {
            product(handle: $handle) {
                metafields(identifiers: $metafieldIdentifiers) {
                namespace
                key
                reference {
                    ... on Metaobject {
                    fields {
                        references(first: 50) {
                        nodes {
                            ... on Metaobject {
                            fields {
                                key
                                value
                            }
                            }
                        }
                        }
                    }
                    }
                }
                }
            }
            }`;

        // Build and array of metafield identifiers that have the reference type "set_of_colors"
        let metafieldIdentifiers: Array<MetafieldIdentifier> = this.metafields ? 
            Array.from(this.metafields.values())
            .filter(mf => mf.referenceType === "set_of_colors")
            .map(mf => mf.identifier) : [];

        if(metafieldIdentifiers.length === 0) {
            console.warn(`No metafields with reference type 'set_of_colors' found for product:  ${this.handle}`);
            return colors; // Return empty map if no metafields found
        }

        // Check `_cachedProductColors` to see if we already have the colors
        if (this._cachedProductColors) {
            // Loop through the metafield identifiers and return the cached colors if it exists
            for (const identifier of metafieldIdentifiers) {
                const cachedColors = this._cachedProductColors.get(identifier.toString());
                if (cachedColors) {
                    colors.set(identifier.toString(), cachedColors);
                }
            }
        }
        // Loop through `colors` and remove any metafield identifiers we just got from the cache
        metafieldIdentifiers = metafieldIdentifiers.filter((id: MetafieldIdentifier) => !colors.has(id.toString()));

        if(metafieldIdentifiers.length === 0) {
            // If we have no metafield identifiers left return our colors
            return colors;
        }

        const {data, errors} = await Shopify.getInstance().request(query, {
            variables: {
                handle: this.handle,
                metafieldIdentifiers: metafieldIdentifiers
            }
        });

        if(!!errors) {
            console.error(errors);
            throw new Error(`Query to get metafield colors for product failed with an error, ${errors.graphQLErrors?.map(e => e.message).join(", ")}`);
        }

        // Extract ProductColors from the `data`
        const metafieldsData = data?.product?.metafields ?? [];
        for (const metafield of metafieldsData) {
            const namespace = metafield.namespace;
            const key = metafield.key;
            const identifier = new MetafieldIdentifier(namespace, key);

            const referenceFields = metafield.reference?.fields ?? [];
            const productColors = new ProductColors();

            for (const field of referenceFields) {

                const colorReferences = field.references?.nodes ?? [];
                for (const refNode of colorReferences) {
                    // Each refNode.fields is an array of { key, value }
                    let colorName = null;
                    let colorHex = null;
                    for (const field of refNode.fields) {
                        if (field.key === "color_name") {
                            colorName = field.value;
                        }
                        else if (field.key === "color") {
                            colorHex = field.value;
                        }
                    }
                    if (colorName && colorHex) {
                        productColors.colors.push(new NamedColor(colorName, colorHex));
                    }
                }
            }

            colors.set(identifier.toString(), productColors);

            // Add the product colors to the cache
            // This will allow us to reuse the colors in the future without querying again
            if (!this._cachedProductColors) {
                this._cachedProductColors = new Map<string, ProductColors>();
            }
            this._cachedProductColors.set(identifier.toString(), productColors);
        }

        return colors;
    }
}

// Get all variants for the a product by handle
export async function getVariantsForProduct(productHandle: string) : Promise<Map<string, Variant>> {
    const query = `
        query getProductVariants($handle: String!, $cursor: String) {
            product(handle: $handle) {
                id
                title
                variants(first: 10, after: $cursor) {
                    pageInfo {
                        hasNextPage
                    }
                    edges {
                        cursor
                        node {
                            id
                            price {
                                amount
                                currencyCode
                            }
                            selectedOptions {
                                name
                                value
                            }
                        }
                    }
                }
            }
        }`;

    let allVariants: Map<string, Variant> = new Map<string, Variant>();
    let hasNextPage = true;
    let cursor: string | null = null;
    const shopify = Shopify.getInstance();

    while (hasNextPage) {
        const { data, errors } = await shopify.request(query, {
            variables: {
                handle: productHandle,
                cursor: cursor,
            },
        });

        if (!!errors) {
            console.error(errors);
            throw new Error("Could not get variants for product");
        }

        const product = data?.product;
        if (!product) {
            throw new Error("Product not found");
        }

        const variants = product.variants;
        for (let edge of variants.edges) {
            const variantObj = edge.node;
            const valueAmount = Number.parseFloat(variantObj.price.amount);
            const variantPrice = new Amount(valueAmount, variantObj.price.currencyCode);
            const selectedOptions: Array<{name: string, value: string}> = variantObj.selectedOptions;
            const variant = new Variant(variantObj.id, variantPrice, selectedOptions);
            allVariants.set(variant.id, variant);
        }
        // Check if there are more variants to fetch
        hasNextPage = variants.pageInfo.hasNextPage;
        cursor = variants.edges.length > 0 ? variants.edges[variants.edges.length - 1].cursor : null;
    }

    return allVariants;
}

export async function getProductByHandle(handle: string) : Promise<Product> {
    const query = `
        query getProductByHandle($handle: String!) {
            productByHandle(handle: $handle) {
                title
                description
                handle
                featuredImageThumb: featuredImage {
                    url(transform: { maxHeight: 300, maxWidth: 300, crop: CENTER })
                }
                featuredImageHero: featuredImage {
                    url(transform: { maxHeight: 700, maxWidth: 500, crop: CENTER })
                }
                images(first: 20) {
                    edges {
                        node {
                            url(transform: { maxHeight: 700, maxWidth: 500, crop: CENTER })
                        }
                    }
                }
                priceRange {
                    minVariantPrice {
                        amount
                        currencyCode
                    }
                }
                variants(first: 1) {
                    edges {
                        node {
                            id
                            price {
                                amount
                                currencyCode
                            }
                            selectedOptions {
                                name
                                value
                            }
                        }
                    }
                }
                metafields(identifiers: [
                    {namespace: "invite", key: "card_stock_color"},
                    {namespace: "invite", key: "ink_color_1"},
                    {namespace: "invite", key: "ink_color_2"},
                    {namespace: "invite", key: "envelope_colors"}
                    ]) {
                    namespace
                    key
                    reference {
                        ... on Metaobject {
                            type
                        }
                    }
                }
            }
        }`;

    const {data, errors, extensions} = await Shopify.getInstance().request(query, {
        variables: {
            handle: handle,
        },
    });

    if(!!errors) {
        console.error(errors);
        throw new Error("Could not get product by handle");
    }

    const productResult = data.productByHandle;
    const price = new Amount(productResult.priceRange.minVariantPrice.amount, productResult.priceRange.minVariantPrice.currencyCode);

    // Add feature product images in both thumbnail and hero sizes
    const product = new Product(productResult.title, productResult.description, price, handle);
    product.featuredImageThumb = productResult.featuredImageThumb?.url;
    product.featuredImageHero = productResult.featuredImageHero?.url;

    // Add metafields to the product
    for(let metafield of productResult.metafields) {
        const metafieldId = `${metafield.namespace}.${metafield.key}`;
        const metafieldObj = new Metafield(metafield.namespace, metafield.key, metafield.reference.type);
        product.metafields.set(metafieldObj.identifier.toString(), metafieldObj);
    }

    // Add all images to the products `images` array
    for(let i = 0; i < productResult.images.edges.length; i++) {
        const image = productResult.images.edges[i].node.url;
        product.images.push(image);
    }

    // Add the first variant if available
    if (productResult.variants.edges.length > 0) {
        const firstVariantNode = productResult.variants.edges[0].node;
        const firstVariantPrice = new Amount(firstVariantNode.price.amount, firstVariantNode.price.currencyCode);
        const selectedOptions: Array<{name: string, value: string}> = firstVariantNode.selectedOptions;
        const firstVariant = new Variant(firstVariantNode.id, firstVariantPrice, selectedOptions);
        product.featuredVariant = firstVariant;

        // Add the featured options for this variant
        product.featuredOption = new Map<string, Set<string>>();
        for(let j = 0; j < selectedOptions.length; j++) {
            const selectedOption = selectedOptions[j];
            if(!product.featuredOption?.has(selectedOption.name)) {
                product.featuredOption?.set(selectedOption.name, new Set<string>());
            }
            product.featuredOption?.get(selectedOption.name)?.add(selectedOption.value);
        }
    }

    //const colors1 = await product.getColors();
    //const colors2 = await product.getColors();

    return product;
}